import { FC, useEffect, useState } from "react"
import { useUser } from "lib/supabase/auth"
import { getDashboardsFromLocalStorage, useForkDashboard, useSelectDashboards } from "reactQuery/dashboard"
import { Dashboard } from "domainModels/dashboard"
import { getGroupsFromLocalStorage } from "reactQuery/group"
import { getWidgetsFromLocalStorage } from "reactQuery/widget"
import { Widget } from "domainModels/widget"
import { Group } from "domainModels/group"
import { Button } from "components/core/button"
import { Loading } from "components/core/modal/loading"
import { InputLabel } from "components/core/inputLabel"
import { Formik } from "formik"
import { Toggle } from "components/core/toggle"

interface ExistingData {
  widgets: Widget[] | null
  groups: Group[]
}

// We need to create a new dashboard if the user is logged in but doesn't have a primary dashboard yet.
export const InitialDashboard: FC = () => {
  const { user, userDetails } = useUser()
  const { data: dashboards, isFetched: dashboardFetched } = useSelectDashboards()
  const [existingData, setExistingData] = useState<ExistingData | null>()
  const { mutateAsync: forkDashboard, isLoading: isForkingDashboard } = useForkDashboard()

  useEffect(() => {
    if (!dashboards?.length && user && dashboardFetched && userDetails) {
      const widgets = getWidgetsFromLocalStorage()
      const groups = getGroupsFromLocalStorage()

      if (widgets || groups.length) {
        setExistingData({
          widgets,
          groups,
        })
      } else {
        setExistingData(null)
      }
    }
  }, [dashboardFetched, dashboards?.length, user, userDetails])

  if (existingData === undefined) {
    return null
  } else if (isForkingDashboard) {
    return <Loading />
  }

  return (
    <div className="bg-whitge dark:bg-gray-900 h-fuhll">
      <div className="px-4 py-16 md:px-6 md:py-24 md:grid md:place-items-center lg:px-8">
        <div className="max-w-max mx-auto">
          <main className="md:flex">
            <p className="text-4xl font-extrabold text-indigo-600 md:text-5xl">👋 Welcome!</p>
            <Formik
              initialValues={{
                dashboardName: getDashboardsFromLocalStorage()?.find((d) => d.is_primary)?.name,
                useExistingData: true,
              }}
              validate={async (values) => {
                const errors: {
                  [key: string]: string
                } = {}

                if (!values.dashboardName || (values.dashboardName && values.dashboardName.length < 3)) {
                  errors.name = "Your dashboard name should contain at least 3 characters"
                }

                return errors
              }}
              onSubmit={async (values) => {
                const widgets = getWidgetsFromLocalStorage()
                const groups = getGroupsFromLocalStorage()

                if (user && values.dashboardName) {
                  await forkDashboard({
                    dashboard: new Dashboard({
                      name: values.dashboardName,
                      user_id: user.id,
                      is_primary: true,
                      is_private: false,
                      is_forked: false,
                      is_password_protected: false,
                      owner_name: userDetails?.full_name ?? "",
                      owner_avatar: userDetails?.avatar_url,
                    }),
                    widgets: values.useExistingData && widgets ? widgets : [],
                    groups: values.useExistingData && groups ? groups : [],
                  })
                }
              }}
            >
              {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, isValid }) => {
                return (
                  <form onSubmit={handleSubmit} autoComplete="off" className="md:ml-6">
                    <div className="md:border-l md:border-gray-200 md:dark:border-gray-800 md:pl-6">
                      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-gray-200 tracking-tight">
                        Create your first dashboard
                      </h1>

                      <div className="my-3 max-w-sm">
                        <InputLabel
                          id="dashboardName"
                          type="text"
                          minLength={3}
                          label="Dashboard name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.dashboardName}
                          errors={errors.dashboardName && touched.dashboardName ? errors.dashboardName : null}
                        />
                      </div>

                      {existingData ? (
                        <>
                          <p className="mt-1 mb-3 text-sm text-gray-500">
                            It looks like you already visited the dashboard page and maybe performed some changes before
                            signing up.
                            <br />
                            You can choose whether to start with the already existing data or start with a blank canvas.
                          </p>
                          <Toggle
                            id="useExisting"
                            label={
                              <>
                                <span>Use existing data</span>
                              </>
                            }
                            initiallyEnabled={values.useExistingData}
                            onChange={(enabled) => setFieldValue("useExistingData", enabled)}
                          />
                        </>
                      ) : (
                        <p className="mt-1 text-base text-gray-500">
                          You can choose to use our default dashboard or start from scratch
                        </p>
                      )}
                    </div>
                    <div className="mt-8 flex space-x-3 md:border-l md:border-transparent md:pl-6">
                      <Button type="submit" disabled={!isValid} variant="primary" size="big">
                        Create dashboard
                      </Button>
                    </div>
                  </form>
                )
              }}
            </Formik>
          </main>
        </div>
      </div>
    </div>
  )
}
